export const SUBSCRIPTION_LAYOUTS = ['Concrete', 'Minimalist', 'LoginlessFlat', 'LoginlessGrouped', 'Lavish'];

export const DEFAULT_LOGINLESS_SETTINGS: Record<string, string> = {
  pageTitle: 'Upgrade to Premium',
  planSelectionHeading: 'Select Your Plan',
  orderSummaryHeading: 'Order Summary',
  totalLabel: 'Total',
  taxNote: '',
  descriptionTitle: "What's Included",
  loaderMessage: 'Oops, it seems plans are taking longer than expected to load. Thank you for your patience!',
  thankYouPageTitle: 'Subscription Successful',
  thankYouPageDescription:
    'Your subscription is active. Just log in or create your account to start enjoying your premium content.',
  continueReading: 'Continue'
};

export const DEFAULT_STANDARD_SETTINGS: Record<string, string> = {
  pageTitle: 'Discover the Power of News Subscription Plans',
  pageDescription:
    'Unlock premium news content and stay informed with our exclusive subscription plan. Get access to in-depth stories and expert analysis.',
  subscribeButton: 'Subscribe Now',
  descriptionTitle: "What's Included",
  loaderMessage: 'Oops, it seems plans are taking longer than expected to load. Thank you for your patience!',
  thankYouPageTitle: 'Thank you for placing your order!',
  thankYouPageDescription:
    'We kindly ask for your patience as we process it (approx. 15min). Please check your email for further details and updates.',
  continueReading: 'Continue Reading',
  goToProfile: 'Go to Profile',
  goToHome: 'Go to Home'
};

export const buildEffectiveSubscription = (rawSubscription: Record<string, any>, isLoginlessEnabled: boolean) => {
  const rawPageSetting: Record<string, any> = rawSubscription.pageSetting || {};
  const defaults = isLoginlessEnabled ? DEFAULT_LOGINLESS_SETTINGS : DEFAULT_STANDARD_SETTINGS;

  const effectivePageSetting: Record<string, any> = { ...defaults };

  Object.keys(rawPageSetting).forEach(key => {
    const val = rawPageSetting[key];
    // Preserve booleans (false is valid); skip only empty strings, null, undefined
    if (typeof val === 'boolean' || (val !== undefined && val !== null && val !== '')) {
      effectivePageSetting[key] = val;
    }
  });

  return { ...rawSubscription, pageSetting: effectivePageSetting };
};

export interface IDataItems {
  type: string;
  label: string;
  key: string;
  defaultValue?: string | boolean;
  placeHolder?: string;
  darkKey?: string;
}

export const getSubscriptionSetting = (layout: string): IDataItems[] => {
  const displayGroupImage: IDataItems = {
    label: 'Display Group Images',
    key: 'showGroupImg',
    type: 'toggle'
  };

  const displayPlanImage: IDataItems = {
    label: 'Display Plan Images',
    key: 'showPlanImg',
    type: 'toggle'
  };

  const loaderSetting: IDataItems[] = [
    {
      label: 'Separator',
      key: 'separator',
      type: 'separator'
    },
    {
      label: 'Loading Delay Message',
      type: 'quillEditor',
      defaultValue: 'Oops, it seems plans are taking longer than expected to load. Thank you for your patience!',
      key: 'loaderMessage',
      placeHolder: 'Oops, it seems plans are taking longer than expected to load. Thank you for your patience!'
    }
  ];

  const setting: IDataItems[] = [
    {
      label: 'Page Title',
      type: 'quillEditor',
      defaultValue: 'Discover the Power of News Subscription Plans',
      key: 'pageTitle',
      placeHolder: 'Discover the Power of News Subscription Plans'
    },
    {
      label: 'Page Description',
      type: 'quillEditor',
      defaultValue:
        'Unlock premium news content and stay informed with our exclusive subscription plan. Get access to in-depth stories and expert analysis.',
      key: 'pageDescription',
      placeHolder:
        'Unlock premium news content and stay informed with our exclusive subscription plan. Get access to in-depth stories and expert analysis.'
    },
    {
      label: 'Subscribe Button Text',
      placeHolder: 'Subscribe Now',
      key: 'subscribeButton',
      defaultValue: 'Subscribe Now',
      type: 'textField'
    },
    {
      label: "What's Included Text",
      placeHolder: "What's Included",
      key: 'descriptionTitle',
      defaultValue: "What's Included",
      type: 'textField'
    },
    {
      type: 'colorPicker',
      label: 'Button Color',
      key: 'buttonColor',
      darkKey: 'darkButtonColor'
    },
    {
      label: 'Display Original Price & Offer',
      key: 'showOfferPrice',
      type: 'toggle'
    }
  ];

  const thankYouPageSettings: IDataItems[] = [
    {
      label: 'Separator',
      key: 'separator2',
      type: 'separator'
    },
    {
      label: 'Thank you page',
      type: 'fieldLabel',
      key: 'fieldLabel2'
    },
    {
      label: 'Title',
      type: 'quillEditor',
      defaultValue: 'Thank you for placing your order!',
      key: 'thankYouPageTitle',
      placeHolder: ''
    },
    {
      label: 'Description',
      type: 'quillEditor',
      defaultValue:
        'We kindly ask for your patience as we process it (approx. 15min). Please check your email for further details and updates.',
      key: 'thankYouPageDescription',
      placeHolder: ''
    },
    {
      label: 'Continue Reading button',
      placeHolder: 'Continue Reading',
      key: 'continueReading',
      defaultValue: 'Continue Reading',
      type: 'textField'
    },
    {
      label: 'Go to Profile button',
      placeHolder: '',
      key: 'goToProfile',
      defaultValue: 'Go to Profile',
      type: 'textField'
    },
    {
      label: 'Go to Home button',
      placeHolder: '',
      key: 'goToHome',
      defaultValue: 'Go to Home',
      type: 'textField'
    }
  ];

  switch (layout) {
    case 'Lavish':
      return [...setting, displayGroupImage, ...loaderSetting, ...thankYouPageSettings];
    case 'Concrete':
      return [...setting, displayPlanImage, ...loaderSetting, ...thankYouPageSettings];
    default:
      return [...setting, ...loaderSetting, ...thankYouPageSettings];
  }
};

export const getLoginlessSettings = (): IDataItems[] => [
  {
    label: 'Page Title',
    type: 'quillEditor',
    defaultValue: 'Upgrade to Premium',
    key: 'pageTitle',
    placeHolder: 'Upgrade to Premium'
  },
  {
    label: 'Select Your Plan',
    placeHolder: 'Select Your Plan',
    key: 'planSelectionHeading',
    defaultValue: 'Select Your Plan',
    type: 'textField'
  },
  {
    label: 'Order Summary',
    placeHolder: 'Order Summary',
    key: 'orderSummaryHeading',
    defaultValue: 'Order Summary',
    type: 'textField'
  },
  {
    label: 'Total',
    placeHolder: 'Total',
    key: 'totalLabel',
    defaultValue: 'Total',
    type: 'textField'
  },
  {
    label: 'Inclusive of all taxes',
    placeHolder: 'Inclusive of all taxes',
    key: 'taxNote',
    defaultValue: 'Inclusive of all taxes',
    type: 'textField'
  },
  {
    label: "What's Included",
    placeHolder: "What's Included",
    key: 'descriptionTitle',
    defaultValue: "What's Included",
    type: 'textField'
  },
  {
    label: 'Display Original Price & Offer',
    key: 'showOfferPrice',
    type: 'toggle'
  },
  {
    type: 'colorPicker',
    label: 'Button Color',
    key: 'buttonColor',
    darkKey: 'darkButtonColor'
  },
  {
    label: 'Loading Delay Message',
    type: 'quillEditor',
    defaultValue: 'Oops, it seems plans are taking longer than expected to load. Thank you for your patience!',
    key: 'loaderMessage',
    placeHolder: 'Oops, it seems plans are taking longer than expected to load. Thank you for your patience!'
  },
  { label: 'Separator', key: 'separatorShipping', type: 'separator' },
  { label: 'Shipping Form', type: 'fieldLabel', key: 'fieldLabelShipping' },
  {
    label:
      'Select the mandatory fields required for the shipping address from the options below. Unselected fields will still appear in the shipping address form, but they will not be mandatory.',
    type: 'hintText',
    key: 'shippingHint'
  },
  { label: 'Full Name', key: 'mandatoryFullName', type: 'checkbox', defaultValue: false },
  { label: 'Street Address', key: 'mandatoryStreetAddress', type: 'checkbox', defaultValue: false },
  { label: 'City', key: 'mandatoryCity', type: 'checkbox', defaultValue: false },
  { label: 'State', key: 'mandatoryState', type: 'checkbox', defaultValue: false },
  { label: 'Country', key: 'mandatoryCountry', type: 'checkbox', defaultValue: false },
  { label: 'Pin', key: 'mandatoryPin', type: 'checkbox', defaultValue: false },
  { label: 'Phone Number', key: 'mandatoryPhone', type: 'checkbox', defaultValue: false },
  { label: 'Email Address', key: 'mandatoryEmail', type: 'checkbox', defaultValue: true },
  { label: 'Separator', key: 'separatorThankYou', type: 'separator' },
  { label: 'Thank You Page', type: 'fieldLabel', key: 'fieldLabelThankYou' },
  {
    label: 'Title',
    type: 'quillEditor',
    defaultValue: 'Subscription Successful',
    key: 'thankYouPageTitle',
    placeHolder: ''
  },
  {
    label: 'Description',
    type: 'quillEditor',
    defaultValue:
      'Your subscription is active. Just log in or create your account to start enjoying your premium content.',
    key: 'thankYouPageDescription',
    placeHolder: ''
  },
  {
    label: 'Continue Button',
    placeHolder: 'Continue',
    key: 'continueReading',
    defaultValue: 'Continue',
    type: 'textField'
  }
];
